import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Star, SlidersHorizontal } from 'lucide-react';
import { getFilteredEstablishments } from '../helpers/establishments';
import { getCategories } from '../helpers/categories';
import { getReviews } from '../helpers/reviews';
import { API_BASE_URL } from '../helpers/api';
import usePageTitle from '../hooks/usePageTitle';
import '../css/Filter.css';
import { renderHTML, truncateText } from '../hooks/tools';

const Filter = () => {
  usePageTitle('Filtrer');
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [establishments, setEstablishments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [reviewStats, setReviewStats] = useState({});
  const [filters, setFilters] = useState({
    price: '',
    city: '',
    category: searchParams.get('category') || '',
    search: '',
    sort: ''
  });
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 12,
    pageCount: 0,
    total: 0
  });

  // Price options
  const priceOptions = ['Gratuit', '€', '€€', '€€€'];

  // Sorting options
  const sortOptions = [
    { value: 'price:asc', label: 'Prix croissant' },
    { value: 'price:desc', label: 'Prix décroissant' }
  ];



  // Initial data load - runs once and when URL params change
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        
        // Load static data first
        const [categoriesData, reviewsData] = await Promise.all([
          getCategories(),
          getReviews()
        ]);

        // Process reviews
        const stats = {};
        reviewsData?.forEach(review => {
          if (!review?.establishment?.documentId) return;
          const estId = review.establishment.documentId;
          if (!stats[estId]) {
            stats[estId] = { count: 0, sum: 0 };
          }
          stats[estId].count++;
          stats[estId].sum += review.rating || 0;
        });
        
        setReviewStats(stats);
        setCategories(categoriesData);

        // Load establishments with URL params if any
        const { data, pagination: paginationData } = await getFilteredEstablishments(
          filters,
          1,
          pagination.pageSize
        );

        // Extract unique cities
        const uniqueCities = [...new Set(data.map(est => est.city))].sort();
        setCities(uniqueCities);
        
        setEstablishments(data);
        setPagination(paginationData);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [searchParams]); // Reload when URL params change

  // Handle filter changes
  useEffect(() => {
    const fetchEstablishments = async () => {
      try {
        setIsLoading(true);
        const { data, pagination: paginationData } = await getFilteredEstablishments(
          filters,
          1,
          pagination.pageSize
        );
        setEstablishments(data);
        setPagination(prev => ({
          ...paginationData,
          pageSize: prev.pageSize
        }));
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchEstablishments();
  }, [filters]);  

  const handlePageChange = async (newPage) => {
    window.scrollTo(0, 0);
    try {
      setIsLoading(true);
      const { data, pagination: paginationData } = await getFilteredEstablishments(
        filters,
        newPage,
        pagination.pageSize
      );
      setEstablishments(data);
      setPagination(paginationData);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      price: '',
      city: '',
      category: '',
      search: '',
      sort: ''
    });
  };

  const getEstablishmentRating = (establishmentId) => {
    const stats = reviewStats[establishmentId];
    if (!stats || stats.count === 0) return { average: 0, count: 0 };
    return {
      average: parseFloat((stats.sum / stats.count).toFixed(1)),
      count: stats.count
    };
  };

  return (
    <div className="filter-page">
      {/* Rest of your JSX remains the same until the results grid */}
      <div className="filter-hero">
        <div className="container">
          <h1>Filtrer les établissements</h1>
          <div className="search-container">
            <Search size={20} />
            <input
              type="text"
              placeholder="Rechercher un établissement..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      <div className="container">
        <div className="filter-content">
          <button 
            className="filters-toggle-btn"
            onClick={() => setIsFiltersPanelOpen(true)}
          >
            <SlidersHorizontal size={20} />
            Filtres
          </button>

          <div 
            className={`filters-overlay ${isFiltersPanelOpen ? 'open' : ''}`}
            onClick={() => setIsFiltersPanelOpen(false)}
          />

          <div className={`filters-sidebar ${isFiltersPanelOpen ? 'open' : ''}`}>
            <button 
              className="filters-close-btn"
              onClick={() => setIsFiltersPanelOpen(false)}
            >
              <i className="bi bi-x-lg" />
            </button>

            {/* Price Filters */}
            <div className="filters-section">
              <h3>Prix</h3>
              <div className="price-filters">
                {priceOptions.map(price => (
                  <button
                    key={price}
                    className={`price-filter ${filters.price === price ? 'active' : ''}`}
                    onClick={() => handleFilterChange('price', filters.price === price ? '' : price)}
                  >
                    {price}
                  </button>
                ))}
              </div>
            </div>

            {/* City Filter */}
            <div className="filters-section">
              <h3>Ville</h3>
              <select
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
                className="filter-select"
              >
                <option value="">Toutes les villes</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div className="filters-section">
              <h3>Catégorie</h3>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="filter-select"
              >
                <option value="">Toutes les catégories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort Options */}
            <div className="filters-section">
              <h3>Trier par</h3>
              <select
                value={filters.sort}
                onChange={(e) => handleFilterChange('sort', e.target.value)}
                className="filter-select"
              >
                <option value="">Par défaut</option>
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <button 
              className="clear-filters-btn"
              onClick={clearFilters}
            >
              Réinitialiser les filtres
            </button>
          </div>

          {/* Results Grid */}
          <div className="results-container">
            <div className="results-header">
              <p>
                {pagination.total} établissement{pagination.total !== 1 ? 's' : ''} trouvé{pagination.total !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="establishments-grid">
              {isLoading ? (
                <div className="loading">Chargement...</div>
              ) : establishments.length === 0 ? (
                <div className="no-results">Aucun établissement trouvé</div>
              ) : (
                establishments.map(establishment => {
                  const rating = getEstablishmentRating(establishment.documentId);
                  return (
                    <Link
                      to={`/spot/${establishment.documentId}`}
                      key={establishment.id}
                      className="establishment-card"
                    >
                      <div className="card-image">
                        <img
                          src={establishment.images[0]?.url ? API_BASE_URL + establishment.images[0].url : '/placeholder.jpg'}
                          alt={establishment.name}
                        />
                        <span className="price-badge">{establishment.price}</span>
                        <span className="category-badge">
                          <i className={`bi ${establishment.category.icon}`} />
                          {establishment.category.name}
                        </span>
                      </div>
                      <div className="card-content">
                        <h3>{establishment.name}</h3>
                        <div>{renderHTML(establishment.description, true, 120)}</div>
                        <div className="card-meta">
                          <span className="rating">
                            <Star size={16} fill="#ffc107" color="#ffc107" />
                            {rating.average}
                            <small>({rating.count})</small>
                          </span>
                          <span className="city">
                            <i className="bi bi-geo-alt" />
                            {establishment.city}
                          </span>
                        </div>
                        <div className="tags">
                          {establishment.tags.slice(0, 3).map((tag, index) => (
                            <span key={index} className="tag">{tag.name}</span>
                          ))}
                          {establishment.tags.length > 3 && (
                            <span className="tag-more">+{establishment.tags.length - 3}</span>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })
              )}
            </div>

            {pagination.pageCount > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  <i className="bi bi-chevron-left" />
                </button>
                
                {[...Array(pagination.pageCount)].map((_, index) => {
                  const pageNumber = index + 1;
                  
                  // Show first page, last page, current page, and one page before and after current
                  if (
                    pageNumber === 1 ||
                    pageNumber === pagination.pageCount ||
                    pageNumber === pagination.page ||
                    pageNumber === pagination.page - 1 ||
                    pageNumber === pagination.page + 1
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        className={`pagination-btn ${pageNumber === pagination.page ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  }
                  
                  // Show dots if there's a gap
                  if (
                    pageNumber === pagination.page - 2 ||
                    pageNumber === pagination.page + 2
                  ) {
                    return <span key={pageNumber} className="pagination-dots">...</span>;
                  }
                  
                  return null;
                })}

                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pageCount}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filter;